import CurrencyIcon from "./CurrencyIcon"
import EditAlertThreshold from "./EditAlertThreshold"
import { Currency } from "@/types/types"
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react"
import { formatMarketCap, formatPercentage, formatPrice } from "@/utils/format"
import { InformationCircleIcon } from "@heroicons/react/20/solid"
import clsx from "clsx"
import * as m from "motion/react-m"
import useMeasure from "react-use-measure"
import { useEffect, useRef } from "react"
import useChangeColors from "@/hooks/useChangeColors"

const SearchItem = ({
  currency,
  onSelect,
}: {
  currency: Currency
  onSelect: ({
    currency,
    threshold,
  }: {
    currency: Currency
    threshold: number
  }) => Promise<void>
}) => {
  const { textColor } = useChangeColors(currency.change_pct_24h)
  const [ref, bounds] = useMeasure()
  const hasMounted = useRef(false)

  useEffect(() => {
    hasMounted.current = true
  }, [])

  return (
    <Disclosure
      as={m.div}
      animate={hasMounted.current ? { height: bounds.height } : {}}
      transition={{ type: "spring", duration: 0.2 }}
      className="group overflow-hidden rounded-lg bg-gray-800"
    >
      <div ref={ref}>
        <div className="relative flex flex-none flex-col justify-between text-lg lg:flex-row lg:items-center">
          <div className="flex h-12 items-center pr-16 pl-4 lg:h-16 lg:flex-1 lg:pr-2">
            <div className="flex-none">
              <CurrencyIcon src={currency.logo_url} />
            </div>
            <div className="ml-4 flex-1 truncate lg:max-w-[94px]">
              {currency.symbol}
            </div>
            <DisclosureButton className="h-8 rounded px-1.5 text-gray-600 hover:text-white">
              <InformationCircleIcon className="size-4.5 shrink-0" />
            </DisclosureButton>
          </div>
          <div className="flex h-12 w-full items-center px-4 pb-2 text-right lg:max-w-[70%] lg:flex-none lg:px-0 lg:pb-0">
            <EditAlertThreshold
              onSubmit={(threshold) => onSelect({ currency, threshold })}
              buttonLabel="Add"
              isSubmitting={false}
            />
          </div>
        </div>
        <DisclosurePanel
          className="grid grid-cols-2 gap-x-2 gap-y-4 px-4 pb-3 sm:grid-cols-4"
          as="dl"
        >
          <div>
            <dt className="text-xs text-gray-500">Name</dt>
            <dd className="truncate text-sm text-white" title={currency.name}>
              {currency.name}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Price</dt>
            <dd className="text-sm text-white">
              {formatPrice(currency.price)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Market cap</dt>
            <dd className="text-sm text-white">
              {formatMarketCap(currency.market_cap)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">24h change</dt>
            <dd className={clsx("text-sm", textColor)}>
              {formatPercentage(currency.change_pct_24h / 100)}
            </dd>
          </div>
        </DisclosurePanel>
      </div>
    </Disclosure>
  )
}

export default SearchItem
